import type {ComponentStageData} from '../../types'
import {
    getParent,
    isHeaderContainer,
    isHeaderFirstRowChild,
    isSuperGridCell,
    isSectionsRootContainer,
    updateTargetLayout
} from '../common'
import {getMeasurementsPointer, getStructurePointer, getTargetLayoutPointer} from '../../conversionDal/utils/pointer'
import {BOTTOM_PADDING, SECTION_MARGIN_CLAMP} from './constants'
import {clamp} from '../../utils/mathUtils'
import _ from 'lodash'

const getBottomMargin = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer, initialSnapshot} = componentStageData

    if (isHeaderFirstRowChild(componentPointer, conversionDal) || isSuperGridCell(componentStageData)) {
        return 0
    }

    const parent = getParent(conversionDal, componentPointer)!
    const {components} = conversionDal.get(parent)!

    if (components.indexOf(componentPointer.id) !== components.length - 1) {
        return 0
    }

    if (isHeaderContainer({componentPointer: parent, conversionDal})) {
        return BOTTOM_PADDING.ROOT
    }

    if (isSectionsRootContainer({conversionDal, componentPointer: parent})) {
        const firstChildMeasurements = initialSnapshot.getValue(
            getMeasurementsPointer(getStructurePointer({id: components[0]}))
        )
        return clamp(
            firstChildMeasurements?.y ?? BOTTOM_PADDING.ROOT,
            SECTION_MARGIN_CLAMP.MIN,
            SECTION_MARGIN_CLAMP.MAX
        )
    }

    return BOTTOM_PADDING.DESCENDANT
}

const getPreviousCompMarginBottom = (componentStageData: ComponentStageData) => {
    const {conversionDal, componentPointer, initialSnapshot} = componentStageData

    if (isHeaderFirstRowChild(componentPointer, conversionDal) || isSuperGridCell(componentStageData)) {
        return 0
    }

    const parent = getParent(conversionDal, componentPointer)!
    const {components} = conversionDal.get(parent)!
    const compIndex = components.indexOf(componentPointer.id)

    if (compIndex === 0) {
        return 0
    }

    const previousComponentLayout = initialSnapshot.getValue(getTargetLayoutPointer({id: components[compIndex - 1]}))

    return previousComponentLayout?.itemLayout?.margins?.bottom?.value ?? 0
}

export const updateComponentVerticalMargins = (
    componentStageData: ComponentStageData,
    topMargin: number,
    bottomMargin: number = getBottomMargin(componentStageData),
    stretchComponent: boolean = false
) => {
    const {conversionDal, componentPointer} = componentStageData
    const {itemLayout} = _.cloneDeep(conversionDal.get(getTargetLayoutPointer(componentPointer)) ?? {})

    updateTargetLayout(componentStageData, {
        itemLayout: _.merge({}, itemLayout, {
            ...(stretchComponent && {
                alignSelf: 'stretch'
            }),
            margins: {
                top: {
                    value: topMargin - getPreviousCompMarginBottom(componentStageData),
                    type: 'spx'
                },
                bottom: {
                    value: bottomMargin,
                    type: 'spx'
                }
            },
            type: itemLayout?.type ?? 'GridItemLayout'
        })
    })
}
